import React, { memo, useContext, useState } from "react";
import { ObjectContext } from "@providers/ObjectProvider";
import Avatar from '@mui/joy/Avatar';
import Chip from '@mui/joy/Chip';
import Box from '@mui/joy/Box';
import Button from '@mui/joy/Button';
import ButtonGroup from '@mui/joy/ButtonGroup';
import Card from '@mui/joy/Card';
import CardContent from '@mui/joy/CardContent';
import CardOverflow from '@mui/joy/CardOverflow';
import CardActions from '@mui/joy/CardActions';
import IconButton from '@mui/joy/IconButton';
import Typography from '@mui/joy/Typography';
import SvgIcon from '@mui/joy/SvgIcon';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import KeyboardArrowRightRoundedIcon from '@mui/icons-material/KeyboardArrowRightRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import AddAlarmRoundedIcon from '@mui/icons-material/AddAlarmRounded';
import Tooltip from '@mui/joy/Tooltip';
import Table from '@mui/joy/Table';
import Stack from '@mui/joy/Stack';
import ObjectRelationshipCard from '@components/objects/ObjectRelationshipCard';
import { gradientTint } from "@utils/gradientTint";
import { sortByDate } from "@utils/sorters";
import ObjectPreview from "./ObjectPreview";
import { formatDate, isValidDate } from "@utils/dates";
import { dueDateClassForTask } from "@utils/dueDateClassForTask";
import { Task } from "@components/Task";

interface ColumnWithValue {
  label: string;
  value: string;//|number|null;
  transformer?: never;
}
interface ColumnWithTransformer {
  label: string;
  value?: never;
  transformer: Function;
}

type IColumn = ColumnWithValue | ColumnWithTransformer;

const ObjectRelationshipUserTasks = memo(function ObjectRelationshipUserTasks({
  relationship = [],
  title,
  tint = null,
  // columns,
  collapsible = true,
}) {
  const [isExpanded, setIsExpanded] = useState(false);

  const perPage = 5;

  const filteredRelationship = relationship.filter(task => !task.completed_at).sort((a, b) => sortByDate(a, b, 'due_date', 'asc'))

  const paginatedRelationship:any[] = (isExpanded || !collapsible) ? filteredRelationship : filteredRelationship.slice(0, perPage);

  const canCollapse = collapsible && relationship.length > perPage;
  const isEmpty = paginatedRelationship.length === 0;

  let paginationTitle = null;

  if(collapsible && !isEmpty) {
    let titleText:any = 'All';
    if(relationship.length > perPage) {
      const ToggleIcon = isExpanded ? KeyboardArrowDownRoundedIcon : KeyboardArrowRightRoundedIcon;
      titleText = <>
        {`${isExpanded ? relationship.length : perPage} of ${relationship.length}`}
        <ToggleIcon sx={{width: '1rem', height: '1rem', mt: '-3px'}} />
      </>;
    }

    paginationTitle = (
      <Typography
        className="pagination"
        sx={theme => ({
          px: 1,
          py: 0.25,
          fontSize: 'sm',
          transition: 'var(--mm-default-transition)',
          backgroundColor: /*theme.palette.background.popup*/'#FFFFFF6B',// tint ? theme.palette[tint][200] : theme.palette.background.level2,
          borderRadius: 'sm',
        })}
      >
        {titleText}
      </Typography>
    );
  }

  return (
    <ObjectRelationshipCard
      unpadded
      empty={isEmpty}
      title={
        <CardOverflow
          sx={theme => ({
            borderTopLeftRadius: 'calc(var(--Card-childRadius) + var(--variant-borderWidth))',
            borderTopRightRadius: 'calc(var(--Card-childRadius) + var(--variant-borderWidth))',
            marginBlockStart: 'var(--CardOverflow-offset)',
            ...gradientTint(theme, tint),
          })}
        >
          <CardActions
            sx={theme => ({
              py: 1.5,
              pointerEvents: canCollapse ? 'all' : 'none',
              cursor: 'pointer',
              userSelect: 'none',
              ':hover .pagination': {
                backgroundColor: '#FFFFFFB8',
              },
            })}
            onClick={() => setIsExpanded(!isExpanded)}
          >
            <Stack
              direction="row"
              justifyContent="flex-start"
              alignItems="center"
              spacing={0.5}
            >
              <Typography sx={theme => ({flex: 1, fontWeight: '600', color: 'white' })}>{title}</Typography>
              {paginationTitle}
            </Stack>
          </CardActions>
        </CardOverflow>
      }
    >
      {/* <CardOverflow> */}
      <Table
        stickyHeader
        stripe="odd"
        // hoverRow
        sx={{
          margin: 0,
          padding: 0,
          tableLayout: 'auto',
          'thead th': {
            borderRadius: '0 !important',
          },
        }}
      >
        <thead>
          <tr>
            <th>Task</th>
            <th>Due</th>
          </tr>
        </thead>
        <tbody>
        {paginatedRelationship.map((row, rowIndex:number) => {
          const dueDate = isValidDate(row.due_date) ? formatDate(row.due_date) : null;

          return (
            <tr key={rowIndex}>
              <td>
                <Task task={row} />
              </td>
              <td className={`min ${dueDateClassForTask(row)}`}>{dueDate}</td>
            </tr>
          )
        })}
        </tbody>
      </Table>
      {/* </CardOverflow> */}

    </ObjectRelationshipCard>
  );
});

export default ObjectRelationshipUserTasks;
